<template lang="pug">
    v-form(style="margin-bottom: -40px;")
        .d-flex.align-center.justify-center.mt-4
            v-btn.mr-2(
                color="primary"
                outlined
                ripple
                @click="displaySlotsView()"
            )
                v-icon(left) mdi-calendar-search
                | Find New Slot
        v-banner(
            class="mb-10 mt-4"
            color="white"
            elevation="1"
            outlined
            rounded
        )
            .d-flex.align-center.justify-center
                v-icon.mr-2(color="primary") mdi-calendar
                span.font-weight-bold.primary--text Appointment:
                span.ml-2(v-if="validSelectedAppointmentDateTime") {{ this.scopedSlot?.friendlyDateTime || "---" }}
                span.ml-2(v-else) ---

        v-row
            v-col
                VehicleCreate(
                    :current-campaign-customer="currentCampaignCustomer"
                    :customer-vehicles="customerVehicles"
                    :handle-add-customer-vehicle="handleAddCustomerVehicle"
                    :first-customer-vehicle="firstCustomerVehicle"
                    :selected-vehicle="selectedVehicle"
                    :set-selected-vehicle="handleSetSelectedVehicle"
                    :show-snackbar="showSnackbar"
                    :handle-toggle-vehicle-form="handleToggleVehicleForm"
                    :show-vehicle-form="showVehicleForm"
                )

        template(v-if="!showVehicleForm")
            v-divider.pb-4

            v-text-field(
                v-if="showCustomerEmailField"
                v-model="formAppointment.emailAddress"
                label="Customer Email *"
                type="email"
                :rules="[rules.required, rules.email]"
                validate-on-blur
            )
            v-textarea(
                v-model="formAppointment.customerComments"
                label='Customer Comments'
                rows="3"
                auto-grow
                clearable
                outlined
                dense
            )
            v-checkbox(
                v-if="showDeliveryContactSameAsCustomer"
                v-model="formAppointment.deliveryContactSameAsCustomer"
                label="Delivery contact same as customer"
                required
            )

            DeliveryContact(
                v-if="showDeliveryContact"
                :is-customer="false"
                :eligible-to-save="eligibleToSaveDeliveryContact"
                :customer-addresses="customerAddresses"
                :primary-customer-address="primaryCustomerAddress"
                v-model="formAppointment.deliveryContact"
                :is-delivery-contact="true"
                :showSnackbar="showSnackbar"
            )

            v-checkbox(
                v-model="formAppointment.notifyCustomer"
                label="Notify customer"
                required
            )
            v-row.d-flex.justify-space-between
                v-col.d-flex.justify-end
                    v-btn(v-if="isEdit" color="warning" small outlined ripple @click="setDispositionDetails('reschedule'); updateIntegrationAppointment(formAppointment)")
                        v-icon mdi-check
                        | Confirm Changes
                    v-btn(v-else :disabled="!eligibleToSave" color="success" small outlined ripple @click='createIntegrationAppointment(formAppointment); setDispositionDetails("create")')
                        v-icon mdi-content-save
                        | Create

</template>

<script lang="ts">
import Vue from 'vue';

// Components
import DeliveryContact from '@/components/communication_center/shared/tabs/integration/customer_form.vue';
import StrLimitTooltipText from '@/components/shared/str_limit_tooltip_text.vue'
import VehicleCreate from '@/components/communication_center/shared/tabs/integration/vehicle_create.vue';

export default Vue.extend({
    data() {
        return {
            formAppointment: {
                appointmentDateTime: null,
                customerComments: null,
                deliveryContact: {
                    companyName: null,
                    customerType: null,
                    email: null,
                    firstName: null,
                    lastName: null,
                    phones: [{
                        phoneNumber: null,
                        phoneType: null,
                        isPrimary: null,
                    }]
                },
                deliveryContactSameAsCustomer: true,
                emailAddress: null,
                notifyCustomer: false,
                organizationId: null,
                serviceAdvisorSid: null,
                shopSid: null,
                transportationTypeSid: null,
                customerVehicleId: null, // volie customer vehicle id
                vehicleMake: null,
                vehicleModel: null,
                vehicleYear: null,
                vehicleVin: null,
            },
            rules: {
                required: (v: any) => !!v || 'Required.',
                email: (v: any) => {
                    if (!v) return true; // Allow empty/null/undefined values
                    const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                    return pattern.test(v) || 'Invalid e-mail.'
                }
            },
            showVehicleForm: false,
            integrationsNeedingDeliveryContact: ["tekion"],
            integrationsNeedingCustomerEmail: ["xtime"],
        }
    },
    methods: {
        handleSetSelectedVehicle(vehicle) {
            if (vehicle) {
                this.formAppointment.vehicleSid = vehicle.vehicle_sid;
                this.formAppointment.customerVehicleId = vehicle.customer_vehicle_id;
                this.formAppointment.vehicleMake = vehicle.vehicle_make;
                this.formAppointment.vehicleModel = vehicle.vehicle_model;
                this.formAppointment.vehicleYear = vehicle.vehicle_model_year;
                this.formAppointment.vehicleVin = vehicle.vin;
            }
            this.setSelectedVehicle(vehicle);
        },
        handleToggleVehicleForm(bool) {
            this.showVehicleForm = bool;
        },
        convertToDateString(milliseconds: number): string {
            let timeZone = this.currentCampaignCustomer?.organization_time_zone_location || "America/New_York";  // default to east coast

            const date = new Date(milliseconds);
            return date.toLocaleDateString('en-CA', { timeZone }); // en-CA gives YYYY-MM-DD format
        },
        convertToTimeString(milliseconds: number | string): string {
            let timeZone = this.currentCampaignCustomer?.organization_time_zone_location || "America/New_York";  // default to east coast

            // If input is a string (ISO format), extract time directly to preserve timezone
            if (typeof milliseconds === 'string') {
                const date = new Date(milliseconds);
                // Use UTC methods to get the time components without timezone conversion
                const hours = date.getUTCHours().toString().padStart(2, '0');
                const minutes = date.getUTCMinutes().toString().padStart(2, '0');
                return `${hours}:${minutes}`;
            }

            // For number input (milliseconds), apply timezone conversion as before
            const date = new Date(milliseconds);
            const options: Intl.DateTimeFormatOptions = {
                timeZone,
                hour: '2-digit',
                minute: '2-digit',
                hour12: false  // This gives us 24-hour format
            };

            const timeFormatter = new Intl.DateTimeFormat('en-US', options);
            return timeFormatter.format(date);
        },
        initAppointment() {
            let appointmentVehicle = this.selectedVehicle || this.firstCustomerVehicle || null;

            // init customer email from customer communication info as a backup if no integration customer is set
            const customerCommunicationInfoEmail = this.customerCommunicationInfo?.customerEmailAddresses?.[0]?.emailAddress || null;
            let emailAddress = this.integrationCustomer?.email || customerCommunicationInfoEmail || null;

            // init form for create
            if (!this.isEdit) {
                this.formAppointment = {
                    appointmentDateTime: this.scopedSlot?.startTimeUtc || null,
                    customerComments: null,
                    deliveryContact: {
                        companyName: null,
                        customerType: null,
                        email: null,
                        firstName: null,
                        lastName: null,
                        phones: [{
                            number: null,
                            phoneType: null,
                        }]
                    },
                    deliveryContactSameAsCustomer: true,
                    notifyCustomer: false,
                    organizationId: this.currentCampaignCustomer?.organization_id || null,
                    serviceAdvisorSid: this.slotFormObject?.integrationServiceAdvisorSid || null,
                    shopSid: this.slotFormObject?.integrationShopSid || null,
                    transportationTypeSid: this.slotFormObject?.integrationTransportationTypeSid || null,
                    customerVehicleId: appointmentVehicle?.customer_vehicle_id || null,
                    vehicleMake: appointmentVehicle?.vehicle_make || null,
                    vehicleModel: appointmentVehicle?.vehicle_model || null,
                    vehicleYear: appointmentVehicle?.vehicle_model_year || null,
                    vehicleVin: appointmentVehicle?.vin || null,
                    emailAddress: emailAddress || null,
                }
            } else if (this.isEdit) {
                this.formAppointment = {
                    appointmentDateTime: this.scopedAppointment?.appointmentDateTime || "",
                    customerComments: this.scopedAppointment?.customer_comments || "",
                    deliveryContact: {
                        companyName: this.scopedAppointment?.delivery_contact?.companyName || null,
                        customerType: this.scopedAppointment?.delivery_contact?.customerType || null,
                        email: this.scopedAppointment?.delivery_contact?.email || null,
                        firstName: this.scopedAppointment?.delivery_contact?.firstName || null,
                        lastName: this.scopedAppointment?.delivery_contact?.lastName || null,
                        phones: [{
                            number: this.scopedAppointment?.delivery_contact?.phones?.[0]?.number || null,
                            phoneType: this.scopedAppointment?.delivery_contact?.phones?.[0]?.phoneType || null,
                        }]
                    },
                    notifyCustomer: this.scopedAppointment?.notify_customer || false,
                    serviceAdvisorSid: this.scopedAppointment?.service_advisor_sid || "",
                    shopSid: this.scopedAppointment?.shop_sid || "",
                    transportationTypeSid: this.scopedAppointment?.transportation_type_sid || "",
                    deliveryContactSameAsCustomer: this.scopedAppointment?.delivery_contact_same_as_customer || true,
                    customerVehicleId: this.scopedAppointment?.customer_vehicle_id || null, // volie customer vehicle id
                    emailAddress: this.scopedAppointment?.email_address || customerCommunicationInfoEmail || null,
                    vehicleMake: this.scopedAppointment?.vehicle_make || null,
                    vehicleModel: this.scopedAppointment?.vehicle_model || null,
                    vehicleYear: this.scopedAppointment?.vehicle_model_year || null,
                    vehicleVin: this.scopedAppointment?.vin || null,
                }
            }
        },
        setDispositionDetails(action) {

            if (this.currentCampaignCustomer?.organization_time_zone_id <= 0) {
                return;
            }

            const integrationAppointmentDetails = {
                appointment_id: 0, // we set this upon successful create/update/cancel
                appointment_datetime_date: this.convertToDateString(this.formAppointment.appointmentDateTime),
                appointment_datetime_time: this.convertToTimeString(this.formAppointment.appointmentDateTime),
                time_zone_id: this.currentCampaignCustomer?.organization_time_zone_id,
                special_instructions: this.formAppointment.customerComments,
                action: action,
            }

            this.setIntegrationAppointmentDetails(integrationAppointmentDetails);
        },
    },
    computed: {
        currentIntegrationIsXtime() {
            return this.currentIntegration === "xtime";
        },
        eligibleToSave() {
            return this.validDeliveryContact && this.validVehicleSelected && !this.showVehicleForm && this.validCustomerEmail;
        },
        isEdit() {
            return this.scopedAppointment?.appointment_sid?.length > 0;
        },
        showDeliveryContact() {
            return !this.formAppointment.deliveryContactSameAsCustomer;
        },
        showDeliveryContactSameAsCustomer() {
            return this.integrationsAcceptingDeliveryContactSameAsCustomer?.includes(this.currentIntegration);
        },
        validSelectedAppointmentDateTime() {
            return this.formAppointment?.appointmentDateTime?.length > 0;
        },
        customerVehicleLabel() {
            return (item) => (`${item?.vehicle_model_year || "---"} ${item?.vehicle_make || "---"} ${item?.vehicle_model || "---"}` || "---");
        },
        validDeliveryContact() {
            if (!this.integrationsNeedingDeliveryContact.includes(this.currentIntegration)) {
                return true;
            }

            if (this.formAppointment && !this.formAppointment?.deliveryContactSameAsCustomer) {
                const dc = this.formAppointment.deliveryContact || null;

                if (!(dc?.firstName?.length > 0)) return false;
                if (!(dc?.lastName?.length > 0)) return false;
                if (!(dc?.email?.length > 0)) return false;
                if (!(dc?.phones?.[0]?.phoneNumber?.length > 0)) return false;
                if (!(dc?.phones?.[0]?.phoneType?.length > 0)) return false;
                if (!(dc?.customerType?.length > 0)) return false;

                // Ensure company name is present if customer type = BUSINESS
                if (dc.customerType === 'BUSINESS') {
                    if (!(dc?.companyName?.length > 0)) return false;
                }
            }

            return true;
        },
        validVehicleSelected() {
            return this.formAppointment?.customerVehicleId > 0;
        },
        validCustomerEmail() {
            if (!this.integrationsNeedingCustomerEmail.includes(this.currentIntegration)) {
                return true;
            }

            return this.formAppointment?.emailAddress?.length > 0 && this.rules.email(this.formAppointment?.emailAddress);
        },
        showCustomerEmailField() {
            return this.integrationsNeedingCustomerEmail.includes(this.currentIntegration);
        },
    },
    components: {
        DeliveryContact,
        StrLimitTooltipText,
        VehicleCreate,
    },
    props: {
        createIntegrationAppointment: Function,
        currentCampaignCustomer: Object,
        currentIntegration: String,
        customerAddresses: Array,
        customerVehicles: Array,
        firstCustomerVehicle: Object,
        integrationVehicles: Array,
        integrationCustomer: Object,
        scopedAppointment: Object,
        scopedSlot: Object,
        setIntegrationAppointmentDetails: Function,
        slotFormObject: Object,
        showSnackbar: Function,
        displaySlotsView: Function,
        updateIntegrationAppointment: Function,
        updateIntegrationVehicles: Function,
        eligibleToSaveDeliveryContact: Boolean,
        primaryCustomerAddress: Object,
        handleAddCustomerVehicle: Function,
        selectedVehicle: Object,
        customerCommunicationInfo: Object,
        setSelectedVehicle: Function,
    },
    created() {
        this.initAppointment();
    },
    destroyed() {
    },
    watch: {
        // //Watch for scope slot change - if user updates, initially gets the value of the date_time from the scopedAppointment
        scopedSlot: {
            handler(newValue) {
                Vue.nextTick(() => {
                    if (newValue?.startTime) {
                        this.formAppointment.appointmentDateTime = newValue.originalSlot?.appointmentDateTime || "";
                    }
                });
            },
            deep: true,
            immediate: true

        },
    },
});
</script>

<style lang="scss" scoped>
.vin-text {
    color: #999;
    font-size: 0.85em;
    font-weight: normal;
}

.vehicle-info {
    color: var(--volie-blue);
    font-weight: bold;
}
</style>